import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  Box,
  Button,
  type ButtonProps,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import type { NavLinkProps } from "react-router";
import { NavLink } from "react-router";

export const SIDEBAR_SELECTED_BG = "#D6EBFF";
export const SIDEBAR_SELECTED_TEXT = "#2563EB";
export const SIDEBAR_BORDER = "#E5E7EB";
export const SIDEBAR_MUTED = "#9CA3AF";

export const Panel = styled(Box)(({ theme }) => ({
  width: "100%",
  borderRadius: 12,
  border: `1px solid ${SIDEBAR_BORDER}`,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  overflow: "hidden",
}));

export const Section = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(0.75),
}));

export const SectionLabel = styled(Typography)(() => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: SIDEBAR_MUTED,
  marginBottom: 4,
}));

export const SectionDivider = styled(Divider)(({ theme }) => ({
  borderColor: SIDEBAR_BORDER,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

export const RowButton = styled(Button)<
  ButtonProps & Partial<NavLinkProps> & { $isActive?: boolean }
>(({ theme, $isActive }) => ({
  minWidth: 0,
  width: "100%",
  justifyContent: "space-between",
  textTransform: "none",
  borderRadius: 8,
  padding: theme.spacing(0.75, 1.25),
  backgroundColor: $isActive ? SIDEBAR_SELECTED_BG : "transparent",
  color: $isActive ? SIDEBAR_SELECTED_TEXT : theme.palette.text.primary,
  fontWeight: $isActive ? 600 : 500,
  fontSize: 14,
  lineHeight: 1.35,

  "&:hover": {
    backgroundColor: $isActive
      ? SIDEBAR_SELECTED_BG
      : theme.palette.action.hover,
  },
}));

export const RowLabel = styled(Typography)(() => ({
  fontSize: "inherit",
  fontWeight: "inherit",
  color: "inherit",
  textAlign: "left",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

export const RowCount = styled(Typography)<{ $isActive?: boolean }>(
  ({ $isActive }) => ({
    fontSize: 13,
    fontWeight: 400,
    color: $isActive ? SIDEBAR_SELECTED_TEXT : SIDEBAR_MUTED,
    flexShrink: 0,
    marginLeft: 8,
  })
);

export const MoreButton = styled(Button)<ButtonProps & { $open?: boolean }>(
  ({ theme, $open }) => ({
    minWidth: 0,
    width: "100%",
    justifyContent: "space-between",
    textTransform: "none",
    borderRadius: 8,
    padding: theme.spacing(0.75, 1.25),
    color: theme.palette.text.primary,
    fontWeight: 500,
    fontSize: 14,

    "& .MuiButton-endIcon": {
      marginLeft: 0,
      transition: theme.transitions.create("transform"),
      transform: $open ? "rotate(180deg)" : "rotate(0deg)",
      color: SIDEBAR_MUTED,
    },

    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  })
);

export const TagsWrap = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));

export const TagPill = styled(NavLink)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  textDecoration: "none",
  borderRadius: 999,
  border: `1px solid ${SIDEBAR_BORDER}`,
  padding: theme.spacing(0.5, 1.25),
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.3,
  color: theme.palette.text.primary,
  backgroundColor: "transparent",

  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

export const StickyDesktop = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: theme.spacing(2),
}));

export const MobileAccordion = styled(MuiAccordion)(() => ({
  boxShadow: "none",
  border: `1px solid ${SIDEBAR_BORDER}`,
  borderRadius: "12px !important",
  "&:before": { display: "none" },
  "&.Mui-expanded": {
    margin: 0,
  },
}));

export const MobileAccordionSummary = styled(MuiAccordionSummary)(
  ({ theme }) => ({
    minHeight: 48,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    "& .MuiAccordionSummary-content": {
      margin: theme.spacing(1.25, 0),
    },
  })
);

export const MobileAccordionDetails = styled(MuiAccordionDetails)(
  ({ theme }) => ({
    padding: theme.spacing(0, 1, 1.5),
  })
);

export const FiltersLabel = styled(Typography)(() => ({
  fontSize: 14,
  fontWeight: 600,
}));
